import { useState } from "react";
import { useProfiles } from "../../hooks/useProfiles.js";
import { useCreateProfile, useUpdateProfile, useDeleteProfile } from "../../hooks/useMutations.js";
import { ProfileTree } from "../../components/ProfileTree/ProfileTree.js";
import { ProfileChecks } from "../../components/ProfileChecks/ProfileChecks.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { Modal } from "../../components/Modal/Modal.js";
import styles from "./Profiles.module.css";

export function ProfilesPage() {
  const { data: profiles, isLoading } = useProfiles();
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>();
  const [editMode, setEditMode] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState("");

  const updateProfile = useUpdateProfile();
  const createProfile = useCreateProfile();
  const deleteProfile = useDeleteProfile();

  const selected = profiles?.find((p) => p.slug === selectedSlug);

  function handleSave(newConfig: Record<string, unknown>) {
    if (!selected) return;
    updateProfile.mutate(
      { id: selected.id, config: newConfig },
      {
        onSuccess: () => {
          setEditMode(false);
          setSavedFeedback(true);
          setTimeout(() => setSavedFeedback(false), 2000);
        },
      },
    );
  }

  function handleDelete() {
    if (!selected) return;
    if (!confirm(`Delete profile "${selected.name}"? This cannot be undone.`)) return;
    deleteProfile.mutate(selected.id, {
      onSuccess: () => {
        setSelectedSlug(undefined);
        setEditMode(false);
      },
    });
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createProfile.mutate(
      { name: newName, checks: {} },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setNewName("");
        },
      },
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.heading}>Profiles</h1>
        <button className={styles.newBtn} onClick={() => setShowCreateModal(true)}>
          + New Profile
        </button>
      </div>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          {isLoading ? (
            <Skeleton height="300px" />
          ) : (
            <ProfileTree
              profiles={profiles ?? []}
              selected={selectedSlug}
              onSelect={(slug) => { setSelectedSlug(slug); setEditMode(false); }}
            />
          )}
        </aside>

        <div className={styles.detail}>
          {!selected ? (
            <div className={styles.empty}>
              Select a profile to view its configuration.
            </div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <h2 className={styles.profileName}>{selected.name}</h2>
                  <code className={styles.slug}>{selected.slug}</code>
                </div>
                <div className={styles.detailActions}>
                  {savedFeedback && <span className={styles.savedMsg}>Saved!</span>}
                  {updateProfile.isError && <span className={styles.errorMsg}>Save failed</span>}
                  <button
                    className={styles.editBtn}
                    onClick={() => setEditMode((e) => !e)}
                  >
                    {editMode ? "Cancel" : "Edit"}
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={handleDelete}
                    disabled={deleteProfile.isPending}
                  >
                    {deleteProfile.isPending ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
              {selected.description && (
                <p className={styles.desc}>{selected.description}</p>
              )}
              <div className={styles.checksSection}>
                <h3 className={styles.checksTitle}>Checks</h3>
                <ProfileChecks
                  config={selected.config ?? {}}
                  editMode={editMode}
                  onSave={handleSave}
                  isSaving={updateProfile.isPending}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New Profile">
        <form onSubmit={handleCreate} className={styles.form}>
          <div className={styles.formField}>
            <label htmlFor="profile-name">Name</label>
            <input
              id="profile-name"
              type="text"
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={styles.input}
              placeholder="My Profile"
            />
          </div>
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={createProfile.isPending}
          >
            {createProfile.isPending ? "Creating…" : "Create"}
          </button>
        </form>
      </Modal>
    </div>
  );
}
